import axios from '@/plugins/axios'
import {
  ExamAttempt,
  ExamAttemptExport,
  EnrollmentAttemptSummary,
  CreateExamAttemptRequest,
  CreateExamAttemptResponse,
  AnswerQuestionRequest,
  AnswerQuestionResponse,
  SubmitExamResponse,
  GetExamAttemptResponse,
  GetExamAttemptExportResponse,
  Question,
} from '../model/exam.model'
import { getLocale } from '@/i18n'

class ExamService {
  /**
   * Create a new exam attempt for a user
   * Returns existing active/passed attempt if one exists
   */
  async createExamAttempt(enrollmentId: string): Promise<ExamAttempt> {
    try {
      const request: CreateExamAttemptRequest = { enrollmentId }
      const response = await axios.post<CreateExamAttemptResponse>(
        '/api/academy/exams/attempts',
        request
      )
      return response.data.data
    } catch (error: any) {
      console.error('Failed to create exam attempt:', error)
      throw new Error(
        error.response?.data?.message || 'Failed to create exam attempt'
      )
    }
  }

  /**
   * Get exam attempt details by ID
   */
  async getExamAttempt(attemptId: string): Promise<ExamAttempt> {
    try {
      const response = await axios.get<GetExamAttemptResponse>(
        `/api/academy/exams/attempts/${attemptId}`
      )
      return response.data.data
    } catch (error: any) {
      console.error('Failed to get exam attempt:', error)
      throw new Error(
        error.response?.data?.message || 'Failed to get exam attempt'
      )
    }
  }

  /**
   * Answer a question in an exam attempt
   */
  async answerQuestion(
    attemptId: string,
    answerData: AnswerQuestionRequest
  ): Promise<ExamAttempt> {
    try {
      const response = await axios.post<AnswerQuestionResponse>(
        `/api/academy/exams/attempts/${attemptId}/answer`,
        answerData
      )
      return response.data.data
    } catch (error: any) {
      console.error('Failed to answer question:', error)
      throw new Error(
        error.response?.data?.message || 'Failed to answer question'
      )
    }
  }

  /**
   * Submit exam for grading
   */
  async submitExam(attemptId: string): Promise<ExamAttempt> {
    try {
      const response = await axios.post<SubmitExamResponse>(
        `/api/academy/exams/attempts/${attemptId}/submit`
      )
      return response.data.data
    } catch (error: any) {
      console.error('Failed to submit exam:', error)
      throw new Error(
        error.response?.data?.message || 'Failed to submit exam'
      )
    }
  }

  /**
   * Calculate total number of questions in exam
   */
  getTotalQuestions(attempt: ExamAttempt): number {
    return attempt.examProgress.reduce(
      (total, section) => total + section.section.tasks.length,
      0
    )
  }

  /**
   * Calculate number of answered questions
   */
  getAnsweredQuestions(attempt: ExamAttempt): number {
    return attempt.examProgress.reduce((total, section) => {
      return (
        total +
        section.answers.filter((answer: any) => {
          if(answer.question.question.type === 'multiple_choice') {
            return answer.options && answer.options.length > 0
          } else  {
            return answer.value !== ""
          }

        }).length
      )
    }, 0)
  }

  /**
   * Check if all questions are answered
   */
  isExamComplete(attempt: ExamAttempt): boolean {
    return attempt.percentage === 1
  }

  /**
   * Get answer for a specific question
   */
  getAnswerForQuestion(
    attempt: ExamAttempt,
    sectionId: string,
    questionId: string
  ): any {
    const section = attempt.examProgress.find(
      (s) => s.section._id === sectionId
    )
    if (!section) return null

    const answer = section.answers.find(
      (a) => a.question._id === questionId
    )
    return answer || null
  }

  /**
   * Check if exam is finished
   */
  isExamFinished(attempt: ExamAttempt): boolean {
    return !!attempt.finishedAt
  }

  /**
   * Format score as percentage
   */
  formatScore(score: number): string {
    return `${Math.round(score * 100)}%`
  }

  /**
   * Get a finished exam attempt with correct answers, for the PDF export template (owner or admin)
   */
  async getExamAttemptForExport(attemptId: string): Promise<ExamAttemptExport> {
    const response = await axios.get<GetExamAttemptExportResponse>(
      `/api/academy/exams/attempts/${attemptId}/export`
    )
    return response.data.data
  }

  /**
   * URL that streams a finished attempt's result PDF, generated on the fly (nothing stored).
   * Language follows the current admin locale.
   */
  examAttemptPdfUrl(attemptId: string): string {
    return `/api/academy/exams/attempts/${attemptId}/export-pdf?lang=${getLocale()}`
  }

  /**
   * List finished exam attempts for an enrollment (admin export picker)
   */
  async getEnrollmentAttempts(enrollmentId: string): Promise<EnrollmentAttemptSummary[]> {
    const response = await axios.get<EnrollmentAttemptSummary[]>(
      `/api/academy/enrollments/${enrollmentId}/attempts`
    )
    return response.data
  }

  /**
   * Human-readable correct answer for a question. Mirrors the backend graders.
   */
  getCorrectAnswerText(question: Question): string {
    switch (question.type) {
      case 'multiple_choice':
        return question.options
          .filter((option) => option.isCorrect)
          .map((option) => option.text)
          .join(', ')
      case 'yes_no':
        return question.correctAnswerCondition?.value ? 'yes' : 'no'
      case 'number':
        return [
          question.correctAnswerCondition?.operator,
          question.correctAnswerCondition?.value,
          question.unit,
        ]
          .filter((part) => part !== undefined && part !== '')
          .join(' ')
      default:
        return ''
    }
  }
}

export default new ExamService()
